<template>
  <div v-if="mode === Mode.METHOD" class="payment-select light">
    <div class="payment-select__header">
      <ExchangeDropDownHeader @close="emit('close')" />
    </div>
    <div class="payment-select__currency-select">
      <div class="payment-select__currency-buttons">
        <CurrencyButtons
          v-model="localCurrency"
          :currencyList="selectedButtonCurrencies"
        />
      </div>
      <div class="payment-select__currency-all-link">
        <a href="#" @click.prevent="mode = Mode.CURRENCY">
          {{ i18n.t("kano.currency-selector.all") }}
          <IconArrowRight />
        </a>
      </div>
    </div>
    <div class="payment-select__sub-heading">
      {{ i18n.t("kano.fiat-payment-select-dropdown.hint-message") }}
    </div>
    <div v-if="methodsState.loading" class="payment-select__loading">
      <ExchangeLoading />
    </div>

    <div v-else-if="isEmptyList" class="payment-select__empty-list">
      <ExchangeDropDownEmptyList />
    </div>
    <PbScroll v-else>
      <div class="payment-select__list">
        <PaymentMethod
          v-for="(item, index) in items"
          data-test-id="payment-method-select"
          :key="index"
          :active="item.id === props.value.id"
          :service="item"
          @click.native="selectMethodHandler(item)"
        />
      </div>
    </PbScroll>
  </div>
  <div v-else-if="mode === Mode.CURRENCY" class="currency-select">
    <div class="currency-select__header">
      <ExchangeDropDownHeader
        :hasBack="true"
        @back="mode = Mode.METHOD"
        @close="emit('close')">
        {{ i18n.t("exchange-form.drop-down.select-currency") }}
      </ExchangeDropDownHeader>
    </div>
    <div class="currency-select__search">
      <FilterInput v-model="searchCurrency" data-testid="currency-select-search" />
    </div>
    <div v-if="methodsState.loading" class="currency-select__loading">
      <ExchangeLoading />
    </div>

    <ExchangeDropDownEmptyFilter v-if="isShowEmptyResult" />
    <PbScroll v-else>
      <div class="currency-select__list">
        <FiatCurrency
          v-for="(item, index) in currencyList"
          data-test-id="fiat-currency-select"
          :key="index"
          :selected="localCurrency === item.currency"
          :currency="item.currency"
          :label="item.label"
          @click.native="selectCurrencyHandler(item.currency)"
        />
      </div>
    </PbScroll>
  </div>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, ref, onMounted, watch, onBeforeMount } from 'vue';
import { useMemoize } from '@vueuse/core';

import CurrencyButtons from './CurrencyButtons.vue';
import ExchangeLoading from '@/components/ExchangeFormV2/ExchangeLoading.vue';
import FiatCurrency from '@kano/components/ExchangeFormV2/FiatCurrency.vue';
import FilterInput from '@kano/components/ExchangeFormV2/FilterInput.vue';
import PbScroll from '@kano/components/ExchangeFormV2/PbScroll.vue';
import PayMethodService from '@kano/services/PayMethodService';
import ExchangeFormService from '@kano/services/ExchangeFormService';
import { i18n } from '@kano/i18n';
import { BaseOptionModel, FiatOptionModel } from '@/models';
import { Currency } from '@kano/types';

import IconArrowRight from '../../../assets/images/exchange-form/arrow-right.svg';
import PaymentMethod from './PaymentMethod.vue';
import ExchangeDropDownHeader from './ExchangeDropDownHeader.vue';
import ExchangeDropDownEmptyFilter from './ExchangeDropDownEmptyFilter.vue';
import ExchangeDropDownEmptyList from './ExchangeDropDownEmptyList.vue';
import { useSimpleList } from '@kano/composables';
import { EventType, pushToDataLayer } from '@paybis/frontend-common-lib/src/services/gtm';
import { Flow, TypeEventMap } from '@paybis/frontend-common-lib/src/types/exchangeForm/exchange-form';

const enum Mode {
  METHOD,
  CURRENCY,
}

const props = defineProps<{
  flow: Flow.FIAT_TO_CRYPTO | Flow.CRYPTO_TO_FIAT
  value: BaseOptionModel
  cryptoCurrency: Currency
}>();

const isEmptyList = computed(() => {
  return methodsState.empty;
});

const localCurrency = ref(props.value.item.currency);

const defaultCurrencies = ['USD', 'EUR'];
const selectedButtonCurrencies = ref([...defaultCurrencies]);

const emit = defineEmits<{
  (event: 'close'): void,
  (event: 'input', option: FiatOptionModel): void,
}>();

const selectMethodHandler = (option: FiatOptionModel) => {
  emit('input', option);
  pushToDataLayer({ event: TypeEventMap[EventType.METHOD_CHANGE][props.flow] });
  emit('close');
};

const selectCurrencyHandler = (currency: Currency) => {
  localCurrency.value = currency;
  pushToDataLayer({ event: TypeEventMap[EventType.FIAT_TICKER_CHANGE][props.flow] });
  mode.value = Mode.METHOD;
  addNewCurrency(currency);
};

// Payment Methods
const listFiatPayoutMethods = useMemoize(
  (from: Currency, to: Currency) => PayMethodService.listFiatPayoutMethods(from, to),
);

const listFiatPaymentMethods = useMemoize(
  (from: Currency, to: Currency) => PayMethodService.listFiatPaymentMethods(from, to),
);

const { state: methodsState, fetch: methodsFetch } = useSimpleList({
  loading: false,
  list: () => {
    if(props.flow === Flow.CRYPTO_TO_FIAT) {
      return listFiatPayoutMethods(props.cryptoCurrency, localCurrency.value);
    }
    return listFiatPaymentMethods(localCurrency.value, props.cryptoCurrency);
  },
});


// Currencies
const fromFiatCurrencies = useMemoize((flow: Flow) => ExchangeFormService.fromFiatCurrencies(flow));
const toFiatCurrencies = useMemoize((flow: Flow) => ExchangeFormService.toFiatCurrencies(flow));
const { state: FiatCurrenciesState, fetch: fetchFiatCurrencies } = useSimpleList({
  list: () => {
    if(props.flow === Flow.CRYPTO_TO_FIAT) {
      return toFiatCurrencies(props.flow);
    }
    return fromFiatCurrencies(props.flow);
  },
});

watch(localCurrency, methodsFetch);

watch(() => methodsState.items, (items) => {
  const method = items.find(method => method.name === props.value.name) ?? items[0];
  if(method && (method.name !== props.value.name || method.currency !== props.value.currency)) {
    emit('input', method);
  }
});

onBeforeMount(() => {
  methodsFetch();
  fetchFiatCurrencies();
});

onMounted(() => {
  addNewCurrency(props.value.item.currency);
});

const mode = ref<Mode>(Mode.METHOD);
onUnmounted(() => mode.value = Mode.METHOD);

const searchMethod = ref('');
onUnmounted(() => searchMethod.value = '');
const items = computed(() => {
  if (searchMethod.value.length) {
    const search = searchMethod.value.toLowerCase();
    return methodsState.items.filter(item => item.name.toLowerCase().includes(search));
  }

  return methodsState.items;
});

const searchCurrency = ref('');

onUnmounted(() => searchCurrency.value = '');

const currencyList = computed(() => {
  if (searchCurrency.value.length) {
    const search = searchCurrency.value.toLowerCase();
    return FiatCurrenciesState.items.filter(item => {
      return item.currency.toLowerCase().includes(search) 
      || item.label.toLowerCase().includes(search)
      || (i18n.t(`asset-names.${item.currency}`) as string).toLocaleLowerCase().includes(search);
    });
  }

  return FiatCurrenciesState.items;
});

const isShowEmptyResult = computed(() => {
  return searchCurrency.value && currencyList.value.length === 0;
});

function addNewCurrency(currency: Currency) {
  if (defaultCurrencies.some(el => el === currency) || !currency) {
    selectedButtonCurrencies.value = [...defaultCurrencies];
    return;
  }
  selectedButtonCurrencies.value = [...defaultCurrencies, currency];
}

watch(mode, method => {
  switch (method) {
  case Mode.METHOD:
    pushToDataLayer({
      event: TypeEventMap[EventType.METHOD_VIEW][props.flow],
      receiveAmountIsShown: methodsState.items.length > 0,
    });
    break;

  case Mode.CURRENCY:
    pushToDataLayer({ event: TypeEventMap[EventType.FIAT_TICKER_VIEW][props.flow] });
    break;

  default:
    break;
  }
}, {
  immediate: true,
});
</script>

<style lang="scss" scoped>
@use '@kanoAssets/styles/breakpoints.scss';

.payment-select {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  gap: 16px;

  @include breakpoints.desktop {
    padding-top: 0;
    gap: 20px;
    height: 512px;
  }

  &__header {
    @include breakpoints.desktop {
      display: none;
    }
  }

  &__currency-select {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 0 24px;
  }

  &__currency-buttons {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  &__currency-all-link {
    flex-shrink: 0;
    width: max-content;
    max-width: 35%;
    color: var(--color-base);
    transition: color 0.2s ease-out;

    @include breakpoints.desktop {
      max-width: none;
    }

    a {
      color: inherit;
      font-size: 14px;
      line-height: 18px;
      font-weight: 600;
      text-decoration: unset;
      display: inline-flex;
      align-items: center;

      @include breakpoints.desktop {
        white-space: nowrap;
        align-items: center;
      }
    }

    svg {
      flex-shrink: 0;
      margin-top: 2px;

      @include breakpoints.desktop {
        margin-top: 0;
      }
    }

    &:hover,
    &:focus {
      color: var(--color-base-48);
    }
  }

  &__empty-list {
    padding: 0 14px;
  }

  &__list {
    margin: 0 14px;
    height: 100%;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__sub-heading {
    padding: 0 24px;
    font-size: 16px;
    line-height: 24px;

    @include breakpoints.desktop {
      font-size: 18px;
      line-height: 26px;
      padding: 8px 24px;
    }
  }
}

.currency-select {
  display: flex;
  flex-direction: column;
  height: 100%;

  @include breakpoints.desktop {
    height: 512px;
  }

  &__list {
    align-content: start;
    margin: 0 14px;
    height: 100%;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
